<template>
  <div :class="classObj" class="app-wrapper">
    <div
      v-if="device==='mobile' && sidebar.opened && !hideLayout"
      class="drawer-bg"
      @click="handleClickOutside"
    />
    <sidebar v-if="!hideLayout" class="sidebar-container" />
    <div
      :class="{
        hasTagsView: !hideLayout,
        'full-screen': hideLayout
      }"
      class="main-container"
    >
      <div v-if="!hideLayout" :class="{ 'fixed-header': false }">
        <navbar />
        <tags-view />
      </div>
      <app-main />
    </div>
    <!-- 来电弹窗：同时支持 TUICallEngine + TIM 自定义消息 -->
    <IncomingCall ref="incomingCall" />
  </div>
</template>

<script>
import { Navbar, Sidebar, TagsView, AppMain } from './components'
import ResizeMixin from './mixin/ResizeHandler'
import IncomingCall from '@/components/videoCall/IncomingCall.vue'
import { loginTIM, getTIM } from '@/utils/timHelper'
import { initTUICallEngine, onIncomingCall } from '@/utils/tuicallHelper'
import { getData } from '@/api/user/authApi'
import TencentCloudChat from '@tencentcloud/chat'

export default {
  name: 'Layout',
  components: { Navbar, Sidebar, AppMain, TagsView, IncomingCall },
  mixins: [ResizeMixin],
  data() {
    return {
      sidebar() { return this.$store.state.app.sidebar },
      device() { return this.$store.state.app.device },
      classObj() {
        return {
          hideSidebar: !this.sidebar.opened,
          openSidebar: this.sidebar.opened,
          withoutAnimation: this.sidebar.withoutAnimation,
          mobile: this.device === 'mobile'
        }
      },
      hideLayout: false
    }
  },
  watch: {
    // 新增：路由切换时，若离开全屏页面则自动恢复菜单
    '$route.path'(newPath, oldPath) {
      const hideLayoutPath = sessionStorage.getItem('hideLayoutPath')
      console.log(hideLayoutPath, '这是地址')
      if (hideLayoutPath && newPath !== hideLayoutPath) {
        sessionStorage.removeItem('hideLayout')
        sessionStorage.removeItem('hideLayoutPath')
        this.hideLayout = false
      }
    }
  },
  created() {
    this.checkHideLayout()
  },
  async mounted() {
    await this.initIM()
    await this.initTUICall()

    // ==================== 开发测试工具 ====================
    window.$videoTest = {
      call: (overrides = {}) => {
        const payload = {
          type: 'VIDEO_CALL_INVITE',
          roomId: 'test-room-' + Date.now(),
          userData: '260526CS00001PR00001',
          sponsor: 'test_caller_' + Date.now(),
          callerInfo: { name: '模拟第三方设备' },
          patientInfo: {
            name: '测试患者张三',
            sexSign: '1',
            birthDate: '1990-01-01',
            contactPhone: '13800138000'
          },
          ...overrides
        }
        this.handleVideoInvite(payload)
        console.log('[VideoTest] 模拟来电已触发', payload)
        return payload
      },
      hangup: () => {
        this.$refs.incomingCall?.hideCall()
        console.log('[VideoTest] 模拟对方已挂断')
      },
      directOpen: (overrides = {}) => {
        const query = {
          videoCall: '1',
          roomId: 'test-room-' + Date.now(),
          diagRecordCode: '260526CS00001PR00001',
          ...overrides
        }
        this.$router.push({
          path: '/business/treatment/medtreatment?type=1',
          query
        })
        console.log('[VideoTest] 已直接跳转诊疗页', query)
      }
    }
    console.log('[VideoTest] 测试工具已挂载到 window.$videoTest')
  },
  methods: {
    /** 检查是否为地址栏直接登录的全屏模式 */
    checkHideLayout() {
      const hideLayout = sessionStorage.getItem('hideLayout') === '1'
      const hideLayoutPath = sessionStorage.getItem('hideLayoutPath')
      // 只有当前路径与记录的全屏路径一致时才隐藏（刷新保持，切换恢复）
      this.hideLayout = hideLayout && hideLayoutPath === this.$route.path
    },

    /** 手动恢复菜单显示（可绑定到页面内的返回/退出按钮） */
    clearHideLayout() {
      sessionStorage.removeItem('hideLayout')
      sessionStorage.removeItem('hideLayoutPath')
      this.hideLayout = false
      this.$router.push('/base/home')
    },

    handleClickOutside() {
      this.$store.dispatch('app/closeSideBar', { withoutAnimation: false })
    },

    async initIM() {
      const userId = this.$store.getters['user/getBaseUserId'] || ''
      if (!userId) return
      try {
        await loginTIM(userId)
        this.bindTIMMessageEvents()
      } catch (err) {
        console.error('[IM] 登录失败', err)
      }
    },

    bindTIMMessageEvents() {
      const tim = getTIM()
      if (!tim || this._timMsgListenerRegistered) return
      this._timMsgListenerRegistered = true

      tim.on(TencentCloudChat.EVENT.MESSAGE_RECEIVED, (event) => {
        const messages = event.data || []
        for (const message of messages) {
          if (message.type !== TencentCloudChat.TYPES.MSG_CUSTOM) continue

          let payload = {}
          try {
            payload = JSON.parse(message.payload.data || '{}')
          } catch (e) { continue }

          if (payload.type !== 'VIDEO_CALL_INVITE') continue

          console.log('【TIM自定义消息】收到视频通话邀请', payload)
          this.handleVideoInvite(payload)
        }
      })
    },

    async initTUICall() {
      const userId = this.$store.getters['user/getBaseUserId'] || ''
      if (!userId) return
      try {
        const userSig = await this.fetchUserSig()
        await initTUICallEngine(1400539979, userId, userSig)
        this.bindTUICallEvents()
      } catch (err) {
        console.error('[TUICall] 初始化失败', err)
      }
    },

    fetchUserSig() {
      const baseUserId = this.$store.getters['user/getBaseUserId'] || ''
      return getData('base/user/user/user/get/getUserSig', 'get', { baseUserId })
        .then(res => {
          const sig = res.data?.data?.userSig || res.data?.userSig || ''
          if (!sig) throw new Error('userSig 为空')
          return sig
        })
    },

    bindTUICallEvents() {
      console.log('[TUICall] 开始注册来电监听...')
      onIncomingCall((event) => {
        console.log('[TUICall] 收到来电事件:', event)
        const callerId = event.callerId || event.sponsor || event.from || event.inviter
        const roomId = event.roomID || event.roomId || event.inviteData?.roomID
        const userData = event.userData || event.inviteData?.userData || event.data

        if (event.callMediaType !== 2 && event.callMediaType !== 'video') {
          console.log('[TUICall] 非视频通话，忽略。callMediaType:', event.callMediaType)
          return
        }

        this.handleVideoInvite({
          roomId: roomId,
          diagRecordCode: userData,
          callerId: callerId,
          callerInfo: { name: '视频通话' }
        })
      })
    },

    handleVideoInvite(payload) {
      this.$refs.incomingCall?.showCall({
        roomId: payload.roomId || payload.roomID,
        diagRecordCode: payload.diagRecordCode || payload.userData,
        callerUserId: payload.callerId || payload.from || payload.sponsor,
        callerInfo: payload.callerInfo || { name: '第三方设备' },
        patientInfo: payload.patientInfo || {}
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import "~@/styles/mixin.scss";
@import "~@/styles/variables.scss";

.app-wrapper {
  @include clearfix;
  position: relative;
  height: 100%;
  width: 100%;
  background-image: url("../assets/list/background.png");
  background-repeat: no-repeat;
  background-size: cover;

  &.mobile.openSidebar {
    position: fixed;
    top: 0;
  }
}

.drawer-bg {
  background: #000;
  opacity: 0.3;
  width: 100%;
  top: 0;
  height: 100%;
  position: absolute;
  z-index: 999;
}

.fixed-header {
  position: fixed;
  top: 0;
  right: 0;
  z-index: 9;
  width: calc(100% - #{$sideBarWidth});
  transition: width 0.28s;
}

.hideSidebar .fixed-header {
  width: calc(100% - 54px)
}

.mobile .fixed-header {
  width: 100%;
}

.back {
  background-color: transparent;
  padding: 16px 20px 20px 0;

  .container {
    border-radius: 6px 6px 6px 6px;
    background-color: #ffffff;
    overflow: hidden;
  }
}

::v-deep .el-notification__group {
  width: 100% !important;
}

::v-deep .el-form--inline .el-form-item {
  margin-right: 0 !important;
}

::v-deep .el-collapse-item__content {
  position: relative;
  left: 0;
  top: 0;
}

// ==================== 新增：全屏模式占满视口 ====================
.main-container.full-screen {
  margin-left: 0 !important;
  width: 100% !important;
  min-height: 100vh;

  // 覆盖 app-main 默认高度（原计算减去了 navbar + tags-view 约 84px）
  ::v-deep .app-main {
    min-height: 100vh;
    padding-top: 0;
    padding-left: 0;
    padding-right: 0;
  }

  // 若内部有固定 padding 的容器，确保占满
  ::v-deep .app-main > div {
    min-height: 100vh;
  }
}
</style>

<style>
.el-notification__group{
    width: 100%!important;
  }
  .el-collapse-item__content{position:relative;left:0px;top:0px;}
</style>
